import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PoolButton, PoolModal, PoolTypography, PreviewImage } from '@synergy/components';
import { StarIcon } from '@synergy/theme';
import { Theme } from '@mui/material';
import { getFileSize, sortByProperty } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';

import { ProductFile, ProductImageFile, isImageFile } from '../../../../../common/models/product-file';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    preview: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        backgroundColor: theme.palette.common.white,
        borderRadius: 4,
        padding: 4,
        '& > img': {
            maxWidth: '100%',
            maxHeight: '100%',
        },
    },
    starred: {
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        color: theme.palette.primary.main,
        '& p': {
            color: theme.palette.primary.main,
            lineHeight: 1.5,
        },
    },
    infoText: {
        color: theme.palette.grey[500],
    },
}));

export interface ProductDetailsFilesTableProps<T extends ProductFile | ProductImageFile> {
    files: T[];
    onEditTitle: (file: T) => void;
    onDownload: (url: string, filename: string) => void;
    onDelete: (file: T) => void;
    onSetAsPreviewImage?: (id: number) => void;
}

export const ProductDetailFilesTable = <T extends ProductFile | ProductImageFile>(
    props: ProductDetailsFilesTableProps<T>
) => {
    const classes = useStyles();
    const [fileToDelete, setFileToDelete] = useState<T | null>(null);
    const locale = useAppSelector(bestLocaleSelector);

    const [sort, setSort] = useState<{ key: string; order: 'asc' | 'desc' }>({ key: 'title', order: 'asc' });

    const handleDelete = (file: T) => {
        setFileToDelete(file);
    };

    const handleDeleteConfirm = () => {
        if (fileToDelete) {
            props.onDelete(fileToDelete);
        }
        setFileToDelete(null);
    };

    const getFileType = (type: string): string => {
        const index = type.indexOf('/');
        if (index < 0) {
            return type;
        }
        return type.substring(index + 1);
    };

    const columns: PaloColumn<T>[] = [
        {
            key: 'preview',
            type: 'icon',
            head: <FormattedMessage id="app.label.previewImage" />,
            cell: file => (
                <PreviewImage url={(isImageFile(file) && file.formats?.['70x70']) || undefined} alt={file.title} />
            ),
        },
        {
            key: 'title',
            type: 'name',
            sortKey: 'title',
            head: <FormattedMessage id="app.label.title" />,
            cell: file => (
                <>
                    <span>{file.title}</span>
                    {isImageFile(file) && file.isPreviewImage && (
                        <div className={classes.starred}>
                            <StarIcon sx={{ fontSize: 17 }} color="primary" />
                            <PoolTypography typography="caption" id="app.label.previewImage" />
                        </div>
                    )}
                </>
            ),
        },
        {
            key: 'fileName',
            type: 'name',
            sortKey: 'fileName',
            head: <FormattedMessage id="app.label.filename" />,
            cell: file => file.fileName,
        },
        {
            key: 'size',
            type: 'fileSize',
            sortKey: 'size',
            head: <FormattedMessage id="app.label.filesize" />,
            cell: file => getFileSize(file.size, locale),
        },
        {
            key: 'type',
            type: 'fileType',
            head: <FormattedMessage id="app.label.filetype" />,
            cell: file => getFileType(file.type),
        },
    ];

    const fileActions = (file: T): PaloRowAction[] => [
        {
            label: <FormattedMessage id="app.product.detail.files.editTitle" />,
            onClick: () => props.onEditTitle(file),
        },
        ...(props.onSetAsPreviewImage
            ? [
                  {
                      label: <FormattedMessage id="app.product.detail.files.setPreviewImage" />,
                      onClick: () => props.onSetAsPreviewImage?.(file.id),
                  },
              ]
            : []),
        {
            label: <FormattedMessage id="app.product.detail.files.download" />,
            onClick: () => props.onDownload(file.path, file.fileName),
        },
        {
            label: <FormattedMessage id="app.product.detail.files.delete" />,
            onClick: () => handleDelete(file),
        },
    ];

    // A file still on its way up has no size and no type to show yet.
    const finishedFiles = sortByProperty({
        data: props.files.filter(file => undefined === file.uploadProgress),
        key: sort.key as keyof T,
        sortDirection: sort.order,
    });

    return (
        <>
            <PaloListView<T, number>
                embedded={true}
                columns={columns}
                items={finishedFiles}
                getRowId={file => file.id}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                sort={sort}
                onSortChange={(key, order) => setSort({ key, order })}
                rowActions={fileActions}
            />

            <PoolModal
                actionChildren={[
                    <PoolButton key="1" onClick={() => setFileToDelete(null)} variant="text" styleType="firstLevel">
                        <FormattedMessage id="app.common.cancel" />
                    </PoolButton>,
                    <PoolButton key="2" onClick={handleDeleteConfirm} color="secondary" styleType="firstLevel">
                        <FormattedMessage id="app.common.delete" />
                    </PoolButton>,
                ]}
                isVisible={!!fileToDelete}
                title={<FormattedMessage id="app.product.detail.files.delete" />}
                handleClose={() => setFileToDelete(null)}
                hasModalTypography={true}
            >
                <PoolTypography
                    id="app.product.detail.files.deleteWarning"
                    values={{ name: fileToDelete?.title ?? fileToDelete?.fileName }}
                />
            </PoolModal>
        </>
    );
};
